import { memo, forwardRef, useCallback, useMemo } from 'react';
import type { InputHTMLAttributes } from 'react';
import { FormLabel, FormHelperText, Box } from '@mui/material';
import { RadioComponentProps, RadioGroupComponentProps } from './Radio.types';
import {
  StyledRadio,
  StyledFormControlLabel,
  StyledRadioGroup,
  StyledFormControl,
} from './Radio.styles';

/**
 * Radio Component
 *
 * Professional radio button component following the green/teal design system.
 * Fully memoized for optimal performance in large ERP applications.
 *
 * @example
 * ```tsx
 * // Individual radio
 * <Radio label="Option 1" value="option1" />
 *
 * // Radio group
 * <RadioGroup
 *   label="Select an option"
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' },
 *   ]}
 *   value={selected}
 *   onChange={(e, value) => setSelected(value)}
 * />
 * ```
 */
export const Radio = memo(
  forwardRef<HTMLButtonElement, RadioComponentProps>(function Radio(
    {
      size = 'md',
      label,
      validationState = 'default',
      disabled = false,
      required = false,
      value,
      name,
      id,
      className,
      'data-testid': dataTestId,
      labelPlacement = 'end',
      ...props
    },
    ref
  ) {
    const radioInputProps: InputHTMLAttributes<HTMLInputElement> & {
      'data-testid'?: string;
    } = {
      ...props.inputProps,
      ...(dataTestId ? { 'data-testid': dataTestId } : {}),
    };

    // Create the radio element
    const radioElement = (
      <StyledRadio
        ref={ref}
        disabled={disabled}
        required={required}
        value={value}
        name={name}
        id={id}
        inputProps={radioInputProps}
        {...props}
      />
    );

    // If there's a label, wrap with FormControlLabel
    if (label) {
      return (
        <StyledFormControlLabel
          radioSize={size}
          validationState={validationState}
          control={radioElement}
          label={label}
          disabled={disabled}
          labelPlacement={labelPlacement}
          className={className}
        />
      );
    }

    return radioElement;
  })
);

Radio.displayName = 'Radio';

/**
 * RadioGroup Component
 *
 * Radio group component for managing multiple radio options.
 *
 * @example
 * ```tsx
 * <RadioGroup
 *   label="Employment Type"
 *   options={[
 *     { value: 'fulltime', label: 'Full-time' },
 *     { value: 'parttime', label: 'Part-time' },
 *     { value: 'contract', label: 'Contract' },
 *   ]}
 *   value={employmentType}
 *   onChange={(e, value) => setEmploymentType(value)}
 * />
 * ```
 */
export const RadioGroup = memo(
  forwardRef<HTMLDivElement, RadioGroupComponentProps>(function RadioGroup(
    {
      size = 'md',
      label,
      helperText,
      validationState = 'default',
      error = false,
      errorMessage,
      successMessage,
      options = [],
      value,
      defaultValue,
      disabled = false,
      required = false,
      onChange,
      name,
      row = false,
      className,
      'data-testid': dataTestId,
      labelPlacement = 'end',
      ...props
    },
    ref
  ) {
    // Determine validation state
    const finalValidationState = useMemo(() => {
      if (error || errorMessage) return 'error';
      if (successMessage) return 'success';
      return validationState;
    }, [error, errorMessage, successMessage, validationState]);

    // Determine helper text
    const finalHelperText = useMemo(() => {
      if (errorMessage) return errorMessage;
      if (successMessage) return successMessage;
      return helperText;
    }, [errorMessage, successMessage, helperText]);

    // Memoize change handler
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>, selectedValue: string) => {
        onChange?.(event, selectedValue);
      },
      [onChange]
    );

    return (
      <StyledFormControl
        ref={ref}
        radioSize={size}
        validationState={finalValidationState}
        error={finalValidationState === 'error'}
        disabled={disabled}
        required={required}
        {...({ component: 'fieldset' } as { component: 'fieldset' })}
        className={className}
      >
        {label && <FormLabel component="legend">{label}</FormLabel>}

        <StyledRadioGroup
          name={name}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          row={row}
          data-testid={dataTestId}
          {...props}
        >
          {options.map((option) => (
            <Box key={option.value}>
              <StyledFormControlLabel
                radioSize={size}
                validationState={finalValidationState}
                value={option.value}
                control={<StyledRadio />}
                label={option.label}
                disabled={disabled || option.disabled}
                labelPlacement={labelPlacement}
              />
              {option.helperText && (
                <FormHelperText
                  sx={{ ml: labelPlacement === 'end' ? '32px' : 0, mt: 0 }}
                >
                  {option.helperText}
                </FormHelperText>
              )}
            </Box>
          ))}
        </StyledRadioGroup>

        {finalHelperText && (
          <FormHelperText error={finalValidationState === 'error'}>
            {finalHelperText}
          </FormHelperText>
        )}
      </StyledFormControl>
    );
  })
);

RadioGroup.displayName = 'RadioGroup';

export default Radio;
